import { TextField } from "@dash/form";
import React, { useEffect, useState } from "react";

import { CustomerAddress } from "@megaron/crm-contracts";
import { Uuid } from "@megaron/uuid";

import { AddressSearchField } from "./AddressSearchField";

export type ListedProfileFields = {
  listedName: string | null;
  listedPhone: string | null;
  listedWebsite: string | null;
};

export type ExtendedCustomerAddress = CustomerAddress & ListedProfileFields;

export type CustomerData = {
  uuid: Uuid;
  address: ExtendedCustomerAddress;
};

type Props = {
  customer?: CustomerData;
  onAddressChange: (value: ExtendedCustomerAddress) => void;
  value?: string;
};

export const LocationPicker: React.FC<Props> = ({ customer, onAddressChange, value }) => {
  const [inputValue, setInputValue] = useState(
    value ?? (customer?.address ? getSearchValueFromAddress(customer?.address) : ""),
  );
  const [localAddress, setLocalAddress] = useState<ExtendedCustomerAddress>(
    customer?.address ?? {
      country: "",
      city: "",
      street: "",
      postalCode: "",
      latitude: null,
      longitude: null,
      googlePlaceId: null,
      listedName: "",
      listedPhone: "",
      listedWebsite: "",
    },
  );

  const handleInputChange = (field: keyof ExtendedCustomerAddress) => (v: string | null) => {
    setLocalAddress((prev) => {
      const next = { ...prev, [field]: v };

      if (
        (field === "street" || field === "city" || field === "country" || field === "postalCode") &&
        !next.googlePlaceId
      ) {
        const searchValue = getSearchValueFromAddress(next);
        setInputValue(searchValue ?? "");
      }

      return next;
    });
  };

  useEffect(() => {
    onAddressChange(localAddress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localAddress]);

  const handleAddressChange = (value: CustomerAddress & Partial<ListedProfileFields>) => {
    const googleFields = {
      listedName: value.googlePlaceId ? customer?.address.listedName ?? null : null,
      listedPhone: value.googlePlaceId ? customer?.address.listedPhone ?? null : null,
      listedWebsite: value.googlePlaceId ? customer?.address.listedWebsite ?? null : null,
    };
    onAddressChange({ ...googleFields, ...value });
    setLocalAddress({ ...googleFields, ...value });
  };

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <AddressSearchField
        value={inputValue}
        onValueChange={setInputValue}
        onChange={handleAddressChange}
        predictionTypes={["geocode", "establishment"]}
      />
      <div css={{ display: "flex", gap: "1rem" }}>
        <TextField label="Kraj" value={localAddress.country} onChange={handleInputChange("country")} />
        <TextField label="Miasto" value={localAddress.city} onChange={handleInputChange("city")} />
      </div>
      <div css={{ display: "flex", gap: "1rem" }}>
        <TextField label="Ulica" value={localAddress.street} onChange={handleInputChange("street")} />
        <TextField label="Kod pocztowy" value={localAddress.postalCode} onChange={handleInputChange("postalCode")} />
      </div>
    </div>
  );
};

const getSearchValueFromAddress = (address: CustomerAddress) => {
  return [address.street, address.postalCode, address.city, address.country].filter(Boolean).join(", ");
};
